import { Fragment, type ReactNode } from 'react';
import { EmptyState, Table, Td, Th } from './ui';

/**
 * One definition of a table, rendered two ways.
 *
 * Above `md` it is the plain table the app has always shown. Below `md` each row
 * becomes a stacked card: the primary column is the headline, the right-aligned
 * column is the figure beside it, and the remaining columns become a compact
 * definition list. No column is dropped — the desktop layout is the regression
 * target, so a card must carry everything the table carries.
 */
export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** The card's headline. Defaults to the first column when none is marked. */
  primary?: boolean;
  /** A right-aligned column becomes the card's figure. Only the first is used. */
  align?: 'left' | 'right';
  /** Kept in the desktop table, omitted from the card (rarely wanted). */
  cardHidden?: boolean;
  /** Overrides the label shown on the card, when the header is not plain text. */
  cardLabel?: string;
  /** Extra classes for the desktop `<td>`. */
  className?: string;
}

export interface ColumnSplit<T> {
  primary: DataColumn<T> | null;
  figure: DataColumn<T> | null;
  details: DataColumn<T>[];
}

/** Split columns into the card's headline, its figure, and its detail rows. */
export function splitColumns<T>(columns: DataColumn<T>[]): ColumnSplit<T> {
  const primary = columns.find((column) => column.primary) ?? columns[0] ?? null;
  const figure = columns.find((column) => column !== primary && column.align === 'right') ?? null;
  const details = columns.filter(
    (column) => column !== primary && column !== figure && !column.cardHidden,
  );
  return { primary, figure, details };
}

function labelFor<T>(column: DataColumn<T>): string {
  if (column.cardLabel) return column.cardLabel;
  return typeof column.header === 'string' ? column.header : column.key;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  empty,
  actions,
  actionsHeader,
  detail,
  rowClassName,
  listClassName = '',
}: {
  rows: T[];
  columns: DataColumn<T>[];
  rowKey: (row: T) => string;
  /** Shown when there are no rows, in both layouts. */
  empty?: ReactNode;
  /** Trailing actions: a column on desktop, a button row on a card. */
  actions?: (row: T) => ReactNode;
  actionsHeader?: ReactNode;
  /**
   * An expanded panel for a row — an inline editor, an audit diff. On desktop it
   * is a full-width row beneath the row; on a card it sits inside the card.
   * Return null/undefined for rows that have nothing to expand.
   */
  detail?: (row: T) => ReactNode;
  rowClassName?: (row: T) => string;
  listClassName?: string;
}) {
  const { primary, figure, details } = splitColumns(columns);
  const columnCount = columns.length + (actions ? 1 : 0);

  return (
    <>
      <div className="hidden md:block">
        <Table>
          <thead>
            <tr>
              {columns.map((column) => (
                <Th key={column.key}>{column.header}</Th>
              ))}
              {actions ? <Th>{actionsHeader}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const detailContent = detail?.(row) ?? null;
              return (
                <Fragment key={rowKey(row)}>
                  <tr className={rowClassName?.(row)}>
                    {columns.map((column) => (
                      <Td key={column.key} className={column.className}>
                        {column.cell(row)}
                      </Td>
                    ))}
                    {actions ? (
                      <Td>
                        <div className="flex flex-wrap items-center gap-2">{actions(row)}</div>
                      </Td>
                    ) : null}
                  </tr>
                  {detailContent ? (
                    <tr className="bg-surface/60">
                      <td colSpan={columnCount} className="px-4 py-4">
                        {detailContent}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            {rows.length === 0 && empty ? (
              <tr>
                <td colSpan={columnCount}>
                  <EmptyState>{empty}</EmptyState>
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      <ul className={`divide-y divide-surface-muted md:hidden ${listClassName}`}>
        {rows.map((row) => {
          const detailContent = detail?.(row) ?? null;
          return (
            <li key={rowKey(row)} className={`py-3 first:pt-0 ${rowClassName?.(row) ?? ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">{primary?.cell(row)}</div>
                {figure ? <div className="shrink-0 text-right">{figure.cell(row)}</div> : null}
              </div>

              {details.length > 0 ? (
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                  {details.map((column) => (
                    <div key={column.key} className="min-w-0">
                      <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-subtle">
                        {labelFor(column)}
                      </dt>
                      <dd className="mt-0.5 text-xs text-ink-body">{column.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions(row)}</div> : null}
              {detailContent ? <div className="mt-3">{detailContent}</div> : null}
            </li>
          );
        })}
        {rows.length === 0 && empty ? (
          <li>
            {typeof empty === 'string' ? (
              <p className="py-8 text-center text-sm text-ink-muted">{empty}</p>
            ) : (
              empty
            )}
          </li>
        ) : null}
      </ul>
    </>
  );
}
